import React from "react";
import {
  formatInvoiceStatus,
  resolveInvoiceStatus,
} from "../../helpers/invoices";
import { getInvoiceStatusPillStyle } from "../../helpers/invoiceStatusUi";
import {
  formatMoneyFromCents,
  PAYMENT_STATE,
  paymentStateLabel,
  summarizeInvoiceSettlement,
} from "../../helpers/invoiceMoney";
import { getInvoiceCollectionStatus } from "../../helpers/invoiceDueStatus";
import { InvoiceDueDateSummary } from "./InvoiceDueStatus";
import { BATCH_SELECTION_CHECKBOX_STYLE } from "../shared/BatchSelectionToolbar";
import "./InvoiceListCard.css";

const ISSUED = new Set(["sent", "paid", "archived"]);

/**
 * Shared invoice row used by Invoice Hub, Project invoices, and Client Financial.
 * Settlement figures come from the payment ledger summary. The pill is the
 * derived payment state. Overdue stays on the due line.
 */
export function InvoiceListCard({
  invoice,
  onOpen,
  showClient = true,
  selectionEnabled = false,
  selected = false,
  onToggleSelect,
}) {
  const status = resolveInvoiceStatus(invoice.status);
  const issued = ISSUED.has(status);
  const settlement = summarizeInvoiceSettlement(invoice);
  const overdue =
    getInvoiceCollectionStatus(invoice) === "overdue" &&
    settlement.outstandingCents > 0;
  const stateLabel = issued
    ? paymentStateLabel(settlement.state)
    : formatInvoiceStatus(status);
  const pillStyle = issued
    ? getInvoiceStatusPillStyle(settlement.state === PAYMENT_STATE.PAID ? "paid" : "sent")
    : getInvoiceStatusPillStyle(status);
  const showSettlement = issued || settlement.paidCents > 0;

  return (
    <div
      className={`invoice-list-card${overdue ? " is-overdue" : ""}`}
      onClick={selectionEnabled ? undefined : () => onOpen(invoice)}
      role={selectionEnabled ? undefined : "button"}
      tabIndex={selectionEnabled ? undefined : 0}
      onKeyDown={
        selectionEnabled
          ? undefined
          : (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onOpen(invoice);
              }
            }
      }
    >
      {selectionEnabled ? (
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleSelect?.()}
          onClick={(event) => event.stopPropagation()}
          style={{ ...BATCH_SELECTION_CHECKBOX_STYLE, marginTop: "4px" }}
          aria-label={`Select ${invoice.invoice_number}`}
        />
      ) : null}
      <div
        className="invoice-list-card__body"
        onClick={selectionEnabled ? () => onOpen(invoice) : undefined}
        role={selectionEnabled ? "button" : undefined}
        tabIndex={selectionEnabled ? 0 : undefined}
        onKeyDown={
          selectionEnabled
            ? (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onOpen(invoice);
                }
              }
            : undefined
        }
      >
        <div className="invoice-list-card__top">
          <div className="invoice-list-card__identity">
            <div className="invoice-list-card__number">{invoice.invoice_number}</div>
            <div className="invoice-list-card__title">{invoice.title || "Untitled invoice"}</div>
            {showClient ? (
              <div className="invoice-list-card__client">
                {invoice.clients?.client_name || "No client"}
              </div>
            ) : null}
            <InvoiceDueDateSummary invoice={invoice} />
          </div>
          <div className="invoice-list-card__aside">
            <div className="invoice-list-card__pill" style={pillStyle}>
              {stateLabel}
            </div>
            <div className="invoice-list-card__total">
              {formatMoneyFromCents(settlement.totalCents)}
            </div>
            {showSettlement ? (
              <>
                <div className="invoice-list-card__paid">
                  Paid {formatMoneyFromCents(settlement.paidCents)}
                </div>
                <div className={`invoice-list-card__balance${overdue ? " is-overdue" : ""}`}>
                  Balance {formatMoneyFromCents(settlement.outstandingCents)}
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
